import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import * as hrmsService from "../../services/hrmsService";
import { useWebSocket } from "../../hooks/useWebSocket";
import { DataTable, ProcBadge, fileSize, linkBtn, openSigned } from "./procUi";
import { AppDropdown, EmptyState, LoadState, PageHeader, PrimaryButton, SlidePanel, formatDateTime, pageStyle, useLoad } from "../../components/ui";

// Conversations between a buyer and a supplier. The same two pieces serve both sides:
//   ChatPanel     one conversation in a side panel (read, write, attach a file)
//   MessagesPage  every conversation, with what is unread, and a way to start a new one
// `service` is procService (buyer) or supplierService (supplier); both have getThread / sendMessage / listThreads / getMessageFileUrl.
// Ids are encrypted afresh in every answer, so a conversation is always opened with the ids its own row carried.

const clean = (ref) => Object.fromEntries(Object.entries(ref).filter(([, v]) => v));
const POLL_MS = 10000;

export function ChatPanel({ service, refQuery, title, subtitle, canWrite = true, onClose }) {
  const key = JSON.stringify(refQuery);
  const { data, loading, error, reload } = useLoad(() => service.getThread(clean(refQuery)), [key]);
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const list = useRef(null);

  useEffect(() => {
    const t = setInterval(reload, POLL_MS);
    return () => clearInterval(t);
  }, [reload]);
  useWebSocket({ onEvent: (e) => e && e.type === "proc_message" && reload() });

  const count = data ? data.messages.length : 0;
  useEffect(() => {
    if (list.current) list.current.scrollTop = list.current.scrollHeight;
  }, [count]);

  const send = async () => {
    if (busy || (!text.trim() && !file)) return;
    setBusy(true);
    try {
      const res = await service.sendMessage(clean(refQuery), text.trim(), file);
      if (res?.success) {
        setText("");
        setFile(null);
        reload();
      } else toast.error(hrmsService.responseMessage(res));
    } catch (err) {
      toast.error(err.message || "The message could not be sent");
    } finally {
      setBusy(false);
    }
  };

  const th = data && data.thread;
  const heading = title || (th ? th.supplier_name || th.buyer_name : "Messages");
  const sub = subtitle !== undefined ? subtitle : th && th.rfq_number ? `${th.rfq_number} · ${th.rfq_title}` : "General conversation";

  return (
    <SlidePanel
      show
      onHide={onClose}
      title={heading}
      width={520}
      footer={
        canWrite ? (
          <div style={{ width: "100%" }}>
            {file ? (
              <div className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: 13 }}>
                <span>{file.name}</span>
                <button type="button" className="btn btn-link text-danger p-0" style={{ fontSize: 13 }} onClick={() => setFile(null)}>Remove</button>
              </div>
            ) : null}
            <textarea
              className="form-control mb-2"
              rows={2}
              maxLength={2000}
              aria-label="Message"
              placeholder="Write a message"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => (e.key === "Enter" && (e.ctrlKey || e.metaKey) ? send() : undefined)}
            />
            <div className="d-flex justify-content-between align-items-center">
              <label className="btn btn-outline-secondary btn-sm mb-0">
                Attach a file
                <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.docx" aria-label="Attach a file" onChange={(e) => { setFile((e.target.files && e.target.files[0]) || null); e.target.value = ""; }} />
              </label>
              <PrimaryButton onClick={send} disabled={busy || (!text.trim() && !file)}>Send</PrimaryButton>
            </div>
          </div>
        ) : null
      }
    >
      <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 12 }}>{sub}</div>
      <LoadState loading={loading && !data} error={error && !data ? error : null} onRetry={reload}>
        <div ref={list} style={{ maxHeight: "60vh", overflowY: "auto" }}>
          {data && data.messages.length === 0 ? <EmptyState title="No messages yet" text={canWrite ? "Write the first message below." : "Nothing has been said yet."} /> : null}
          {(data ? data.messages : []).map((m) => (
            <div key={m.id} className="d-flex mb-3" style={{ justifyContent: m.mine ? "flex-end" : "flex-start" }}>
              <div style={{ maxWidth: "82%", background: m.mine ? "#FCE7EE" : "#F3F4F6", borderRadius: 10, padding: "8px 12px" }}>
                <div style={{ fontSize: 12, color: "#6B7280" }}>{m.mine ? "You" : m.sender_name} · {formatDateTime(m.created_at)}</div>
                {m.message ? <div style={{ fontSize: 14, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{m.message}</div> : null}
                {m.file_id ? (
                  <div style={{ fontSize: 13 }}>
                    <button type="button" style={linkBtn} onClick={() => openSigned(() => service.getMessageFileUrl(m.file_id), toast)}>{m.file_name}</button> <span style={{ color: "#6B7280" }}>{fileSize(m.file_size)}</span>
                  </div>
                ) : null}
                {m.mine && m.seen ? <div style={{ fontSize: 11, color: "#6B7280", textAlign: "right" }}>Seen</div> : null}
              </div>
            </div>
          ))}
        </div>
      </LoadState>
    </SlidePanel>
  );
}

// loadParties() -> [{ label, ref }]: who a new conversation can be started with.
export function MessagesPage({ service, side, loadParties, canWrite = true }) {
  const { data, loading, error, reload } = useLoad(() => service.listThreads(), []);
  const parties = useLoad(() => loadParties(), []);
  const [open, setOpen] = useState(null); // { ref, title }
  useEffect(() => {
    const t = setInterval(reload, 30000);
    return () => clearInterval(t);
  }, [reload]);
  useWebSocket({ onEvent: (e) => e && e.type === "proc_message" && reload() });

  const refOf = (t) => (side === "BUYER" ? { supplier_id: t.supplier_id, rfq_id: t.rfq_id } : { buyer_id: t.buyer_id, rfq_id: t.rfq_id });
  const who = (t) => (side === "BUYER" ? t.supplier_name : t.buyer_name);
  const options = (parties.data || []).map((p, i) => ({ label: p.label, value: i }));
  const rows = data || [];

  return (
    <div style={pageStyle}>
      <PageHeader title="Messages" subtitle={side === "BUYER" ? "Conversations with your suppliers" : "Conversations with your buyers"} />
      {canWrite && options.length ? (
        <div className="mb-3" style={{ maxWidth: 320 }}>
          <AppDropdown id="msg-new" value={null} options={options} filter={options.length > 8} placeholder="Start a new conversation" onChange={(e) => e.value !== null && e.value !== undefined && setOpen({ ref: parties.data[e.value].ref, title: parties.data[e.value].label })} aria-label="Start a new conversation" />
        </div>
      ) : null}
      <LoadState loading={loading} error={error} onRetry={reload}>
        <DataTable
          rows={rows}
          rowKey={(t, i) => i}
          onRowClick={(t) => setOpen({ ref: refOf(t), title: who(t) })}
          empty={<EmptyState title="No conversations yet" text={side === "BUYER" ? "Message a supplier from here, from an RFQ, or from the supplier's page." : "Message a buyer from here, or from an RFQ."} />}
          columns={[
            { label: side === "BUYER" ? "Supplier" : "Buyer", render: (t) => <strong>{who(t)}</strong> },
            { label: "About", render: (t) => (t.rfq_number ? `${t.rfq_number} · ${t.rfq_title}` : "General") },
            { label: "Last message", render: (t) => <span style={{ color: "#374151" }}>{t.last_sender_role === side ? "You: " : ""}{t.last_message}</span> },
            { label: "When", render: (t) => formatDateTime(t.last_at), style: { whiteSpace: "nowrap" } },
            { label: "", render: (t) => (t.unread ? <ProcBadge status="LIVE" label={`${t.unread} new`} /> : null) },
          ]}
        />
      </LoadState>
      {open ? <ChatPanel service={service} refQuery={open.ref} title={open.title} canWrite={canWrite} onClose={() => { setOpen(null); reload(); }} /> : null}
    </div>
  );
}
